import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  OrePagination,
  OreStack,
  OreTable,
  OreTableActions,
  OreTableBody,
  OreTableCaption,
  OreTableContainer,
  OreText,
} from '@edenredespana/oreneta'
import { TableHeadersModel } from '@/Shared/domain/models/TableHeadersModel'
import { TableHead } from '@/Shared/ui/Table'
import { tablePagination } from '@/Shared/ui/sharedTranslation'

type TableHeadProps<T> = {
  title?: string
  count?: string
  tableHeaders: TableHeadersModel[]
  rows: T[]
  recordsPerPage?: number
  withPagination?: boolean
  noRows?: string
  renderRow: (row: T, index: number) => JSX.Element
}

export const SimpleTable = <T extends object>({
  title,
  tableHeaders,
  rows,
  recordsPerPage = 8,
  withPagination = false,
  noRows,
  renderRow,
  count,
}: TableHeadProps<T>): JSX.Element => {
  const [page, setPage] = useState(1)
  const { t } = useTranslation()

  const handlePageChange = (value: number): void => {
    setPage(value)
  }

  const offset = (page - 1) * recordsPerPage
  const shouldShowPagination = withPagination && rows.length > recordsPerPage
  const shouldShowNoRows = rows.length === 0 && noRows && !shouldShowPagination
  const slicedRows = withPagination ? rows.slice(offset, page * recordsPerPage) : rows

  return (
    <OreTableContainer>
      <OreTable>
          {title ? <OreTableCaption title={title} counter={count ?? undefined} /> : null}
        <TableHead tableHeaders={tableHeaders} />
        <OreTableBody>
          {slicedRows.map((row, index) => renderRow(row, index + offset))}
        </OreTableBody>
      </OreTable>
      {shouldShowPagination ? (
        <OreTableActions>
          <OrePagination
            displayPageButtons
            count={rows.length}
            labelNextPage={t(tablePagination.nextPageLabel)}
            labelPreviousPage={t(tablePagination.previousPageLabel)}
            onPageChange={handlePageChange}
            page={page}
            rowsPerPage={recordsPerPage}
            text={t(tablePagination.pageOf)}
            legend={t(tablePagination.rowsShowing)}
          />
        </OreTableActions>
      ) : null}
      {shouldShowNoRows ? (
        <OreTableActions>
          <OreText size="text-sm" tone="neutral-900">
            {noRows}
          </OreText>
        </OreTableActions>
      ) : null}
    </OreTableContainer>
  )
}
